/**
 * Preserves browser state saved by the former single Pinia store when upgrading
 * to the split session, partner, and date-range stores. Runs before store
 * hydration, retaining each field's original localStorage or sessionStorage scope
 * and giving any already-migrated state precedence over legacy values.
 */
type PersistedState = Record<string, unknown>;

const LEGACY_STORE_ID = "store";

class StoreMigrationError extends Error {
    constructor(readonly failures: { scope: string; error: unknown }[]) {
        super(
            `Failed to migrate persisted state in: ${failures.map(({ scope }) => scope).join(", ")}.`,
        );
        this.name = "StoreMigrationError";
    }
}

/**
 * Narrows parsed JSON to a non-null, non-array object suitable for copying state.
 * This checks the container only, not the types of individual stored fields.
 */
function isPersistedState(value: unknown): value is PersistedState {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads the legacy store from the supplied browser storage.
 * Returns null when absent. Invalid JSON or containers throw so the caller can
 * retain the entry and report the failure after attempting both storage scopes.
 */
function readPersistedState(storage: Storage): PersistedState | null {
    const serializedState = storage.getItem(LEGACY_STORE_ID);
    if (serializedState === null) {
        return null;
    }

    const state: unknown = JSON.parse(serializedState);
    if (isPersistedState(state)) {
        return state;
    }
    throw new TypeError("Persisted store state must be an object.");
}

/**
 * Copies only the selected legacy fields into a destination store in the same
 * storage scope, without overwriting fields already present there.
 * Skips writing when no selected fields exist. Parsing, container-validation,
 * and storage errors propagate so the caller does not remove the legacy entry.
 */
function copyState(
    storage: Storage,
    oldState: PersistedState,
    storeId: string,
    keys: string[],
) {
    const migratedState: PersistedState = {};
    for (const key of keys) {
        if (key in oldState) {
            migratedState[key] = oldState[key];
        }
    }

    if (Object.keys(migratedState).length === 0) {
        return;
    }

    const currentSerializedState = storage.getItem(storeId);
    const currentState: unknown = currentSerializedState
        ? JSON.parse(currentSerializedState)
        : {};
    if (!isPersistedState(currentState)) {
        throw new TypeError(
            `Persisted state for "${storeId}" must be an object.`,
        );
    }

    storage.setItem(
        storeId,
        JSON.stringify({ ...migratedState, ...currentState }),
    );
}

/**
 * Distributes legacy state to the split stores before Pinia hydrates them.
 * Removes each legacy entry only after its copies complete successfully.
 * Attempts both storage scopes independently, then throws a combined error with
 * the scope and original error for each failure for the startup boundary to report.
 * Subsequent calls do nothing for storage scopes whose legacy entry is absent.
 */
export function migrateLegacyStoreState() {
    const failures: { scope: string; error: unknown }[] = [];

    try {
        const localState = readPersistedState(localStorage);
        if (localState) {
            copyState(localStorage, localState, "session", [
                "userEmail",
                "permissions",
            ]);
            copyState(localStorage, localState, "partner", ["partnerProfile"]);
            localStorage.removeItem(LEGACY_STORE_ID);
        }
    } catch (error) {
        failures.push({ scope: "localStorage", error });
    }

    try {
        const sessionState = readPersistedState(sessionStorage);
        if (sessionState) {
            copyState(sessionStorage, sessionState, "session", [
                "userEmail",
                "isAuthenticated",
            ]);
            copyState(sessionStorage, sessionState, "dateRange", [
                "dateRange",
                "singleDate",
            ]);
            sessionStorage.removeItem(LEGACY_STORE_ID);
        }
    } catch (error) {
        failures.push({ scope: "sessionStorage", error });
    }

    if (failures.length > 0) {
        throw new StoreMigrationError(failures);
    }
}
